/**
 * The game shell (Phase 5): main menu → track selection → play →
 * completion → track selection, plus settings, How to Play, credits and the
 * track editor, without page reloads.
 *
 * Lifecycle: at most one GameSession or EditorApp exists at a time, and
 * leaving a screen disposes it (engine, physics world, WebGL context,
 * listeners). The app's own listeners are registered once.
 */
import { liveResources, type LiveResourceCounts } from '../debug/LiveResources';
import { AudioEngine, type EffectName } from '../audio/AudioEngine';
import { button, h } from '../editor/dom';
import type { EditorApp } from '../editor/EditorApp';
import { importTrackFile } from '../editor/importExport';
import { titleWithSuffix } from '../editor/model';
import { ProjectStore, newProjectId } from '../editor/ProjectStore';
import type { EngineDom } from '../core/Engine';
import { RendererInitError } from '../rendering/Renderer';
import { initPhysics } from '../simulation/PhysicsWorld';
import { LevelValidationError } from '../world/LevelLoader';
import type { GameMode } from '../gameplay/AttemptManager';
import { ProgressStore, emptyProgress, type TrackProgress } from '../gameplay/ProgressStore';
import { loadSettings, saveSettings, type Settings } from '../ui/Settings';
import { applyLineColour } from '../ui/theme';
import { builtinTracks, developerLevels, localTracks, nextTrack, type CatalogTrack } from './catalog';
import { GameSession, type RunResult, type SessionTestHooks } from './GameSession';
import { ReplayFlow } from './ReplayFlow';
import type { ViewerTestHooks } from '../replay/ReplayViewer';
import { SettingsPanel } from './SettingsPanel';
import { RankedClient } from '../ranked/RankedClient';
import { RankedFlow, type RankedState } from './RankedFlow';
import { devScoresScreen, rankedReady, rankedResult } from './rankedScreens';
import { credits, errorScreen, howToPlay, loadingScreen, mainMenu, pausePanel, readyPanel, resultsPanel, trackBrowser } from './screens';

export type ScreenId = 'menu' | 'tracks' | 'settings' | 'howto' | 'credits' | 'loading' | 'error' | 'play' | 'editor' | 'replays' | 'replay' | 'scores';

export interface GameTestHooks {
  screen(): ScreenId;
  /** Which in-game panel is showing ('' while playing). */
  panel(): string;
  tracks(): { key: string; title: string; source: string; error: string | null; recordKey: string }[];
  open(key: string, mode?: GameMode): Promise<void>;
  session(): SessionTestHooks | null;
  progress(key: string): TrackProgress;
  settings(): Settings;
  lastResult(): RunResult | null;
  audioState(): string;
  audioPlayed(): number;
  /** Live physics worlds and WebGL renderers (development builds only). */
  resources(): LiveResourceCounts;
  /** The open replay viewer, if any. */
  viewer(): ViewerTestHooks | null;
  /** Ranked play of the open session (null when no track is open). */
  ranked(): { enabled: boolean; state: RankedState } | null;
}

export interface GameAppDom {
  shell: HTMLElement;
  editor: HTMLElement;
  /** The legacy game host the editor's playtests use. */
  legacyHost: HTMLElement;
  legacyDom: Omit<EngineDom, 'canvas'>;
  legacyExit: HTMLButtonElement;
}

/** True on devices with no fine pointer and no hover (phones, most tablets). */
export function isTouchOnly(): boolean {
  try {
    return matchMedia('(pointer: coarse)').matches && !matchMedia('(any-pointer: fine)').matches;
  } catch {
    return false;
  }
}

export class GameApp {
  private settings: Settings = loadSettings();
  private readonly progress = new ProgressStore();
  private readonly audio = new AudioEngine();
  private screen: ScreenId = 'menu';
  private readonly screenHost = h('main', { className: 'screen-host' });
  private readonly playLayer = h('div', { className: 'play-layer', hidden: true });
  private readonly gameHost = h('div', { className: 'game-host' });
  private readonly panelHost = h('div', { className: 'panel-host' });
  private session: GameSession | null = null;
  private editor: EditorApp | null = null;
  private settingsPanel: SettingsPanel | null = null;
  private store: ProjectStore | null = null;
  private storeTried = false;
  private local: CatalogTrack[] = [];
  private loadingLocal = false;
  private selected: string | null = null;
  private mode: GameMode = 'full-run';
  private notice = '';
  private lastResult: RunResult | null = null;
  private panel = '';
  /** Where the settings screen returns to. */
  private settingsReturn: 'menu' | 'pause' = 'menu';
  private pauseMessage = '';
  private busy = false;
  private saveNote = '';
  private readonly rankedClient = new RankedClient({ base: (import.meta.env['VITE_XERNOUS_API'] as string | undefined) || undefined });
  private ranked: RankedFlow | null = null;
  /** The run the ranked flow is submitting or has decided. */
  private rankedRun: RunResult | null = null;
  private readonly replays = new ReplayFlow({
    tracks: () => this.allTracks(),
    settings: () => this.settings,
    mount: (id, el) => this.mount(id, el),
    menu: () => this.showMenu(),
    loading: (title, detail) => this.mount('loading', loadingScreen(title, detail)),
    error: (title, message) => this.mount('error', errorScreen(title, message, [['Replays', () => void this.replays.show()], ['Main menu', () => this.showMenu()]])),
    sound: (kind) => this.sound(kind),
  });

  constructor(private readonly dom: GameAppDom) {
    dom.shell.append(this.screenHost, this.playLayer);
    this.playLayer.append(this.gameHost, this.panelHost);
    const unlock = (): void => this.audio.unlock();
    document.addEventListener('pointerdown', unlock, { capture: true });
    document.addEventListener('keydown', unlock, { capture: true });
    document.addEventListener('keydown', (e) => this.onKey(e));
    this.applyShellSettings();
    this.showMenu();
    // The physics engine is its own chunk (its WASM is ~1.7 MB gzipped): fetch it once the menu is up so the
    // first track does not wait for it. A failure here is not cached: the track start retries the load, and
    // explains the error if it fails again.
    setTimeout(() => void initPhysics().catch(() => undefined), 300);
  }

  // ---------------------------------------------------------------- screens

  private mount(id: ScreenId, el: HTMLElement): void {
    this.settingsPanel?.dispose();
    this.settingsPanel = null;
    if (id !== 'replay' && id !== 'loading') this.replays.closeViewer();
    this.screen = id;
    this.dom.shell.dataset['screen'] = id;
    this.screenHost.hidden = false;
    this.playLayer.hidden = true;
    this.screenHost.replaceChildren(el);
    el.querySelector<HTMLElement>('.primary, button')?.focus({ preventScroll: true });
  }

  private sound(name: EffectName = 'ui'): void {
    this.audio.play(name);
  }

  showMenu(): void {
    const all = this.allTracks().filter((t) => t.source === 'builtin');
    const completed = all.filter((t) => this.progressOf(t).completions > 0).length;
    this.mount(
      'menu',
      mainMenu({
        play: () => this.go(() => void this.showTracks()),
        replays: () => this.go(() => void this.openReplays()),
        create: () => this.go(() => void this.openEditor()),
        settings: () => this.go(() => this.showSettings('menu')),
        howTo: () => this.go(() => this.showHowTo()),
        credits: () => this.go(() => this.mount('credits', credits({ back: () => this.goBack(() => this.showMenu()) }))),
        devScores: import.meta.env.DEV ? () => this.go(() => void this.showDevScores()) : undefined,
        touchOnly: isTouchOnly(),
        summary: { completed, total: all.length },
      }),
    );
  }

  async showTracks(): Promise<void> {
    this.renderTracks();
    if (!this.loadingLocal) await this.refreshLocal();
  }

  private renderTracks(): void {
    const tracks = this.allTracks();
    const keep = this.screen === 'tracks' ? this.screenHost.querySelector('.track-list')?.scrollTop ?? 0 : 0;
    this.mount(
      'tracks',
      trackBrowser(tracks, {
        back: () => this.goBack(() => this.showMenu()),
        select: (t) => {
          this.sound();
          this.selected = t.key;
          this.renderTracks();
        },
        play: (t, mode) => this.go(() => void this.openTrack(t, mode)),
        edit: (t) => this.go(() => void this.openEditor(t.projectId)),
        importFile: (f) => void this.importFile(f),
        progress: (t) => this.progressOf(t),
        selected: this.selected,
        mode: this.mode,
        setMode: (m) => {
          this.sound();
          this.mode = m;
          this.renderTracks();
        },
        loadingLocal: this.loadingLocal,
        notice: this.notice,
      }),
    );
    const list = this.screenHost.querySelector('.track-list');
    if (list) list.scrollTop = keep;
    this.screenHost.querySelector<HTMLElement>('.track-card.selected')?.focus({ preventScroll: true });
  }

  showSettings(from: 'menu' | 'pause'): void {
    this.settingsReturn = from;
    const panel = new SettingsPanel({
      settings: () => this.settings,
      change: (next) => this.changeSettings(next),
      resetProgress: () => this.progress.clear(),
      click: () => this.sound(),
    });
    const doneLabel = from === 'pause' ? '← Back to the game' : '← Menu';
    const body = h('section', { className: 'screen screen-settings', dataset: { screen: 'settings' } }, h('div', { className: 'screen-bar' }, button(doneLabel, () => this.closeSettings(), { className: 'back', dataset: { nav: 'back' } }), h('header', { className: 'screen-head' }, h('h1', { textContent: 'Settings' }))), panel.root);
    if (from === 'pause') {
      // Over the paused game: the session stays alive underneath.
      this.settingsPanel?.dispose();
      this.panel = 'settings';
      this.panelHost.replaceChildren(h('div', { className: 'game-panel settings-overlay' }, body));
      this.settingsPanel = panel;
      return;
    }
    this.mount('settings', body);
    this.settingsPanel = panel;
  }

  private closeSettings(): void {
    if (this.settingsPanel?.capturing) return;
    this.sound('uiBack');
    if (this.settingsReturn === 'pause' && this.session) {
      this.settingsPanel?.dispose();
      this.settingsPanel = null;
      this.showPausePanel();
    } else this.showMenu();
  }

  showHowTo(): void {
    this.mount(
      'howto',
      howToPlay(this.settings, {
        back: () => this.goBack(() => this.showMenu()),
        training: () => this.go(() => {
          const t = this.allTracks().find((x) => x.source === 'training');
          if (t) void this.openTrack(t, 'practice');
        }),
      }),
    );
  }

  private showError(title: string, message: string): void {
    this.leavePlay();
    this.mount('error', errorScreen(title, message, [['Back to tracks', () => void this.showTracks()], ['Main menu', () => this.showMenu()]]));
  }

  // ---------------------------------------------------------------- play

  async openTrack(track: CatalogTrack, mode: GameMode): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      this.leavePlay();
      this.selected = track.key;
      this.mode = mode;
      if (!track.level) {
        this.showError(`${track.title} can't be played`, `This track did not compile: ${track.error ?? 'unknown error'}. Open it in the editor to fix it.`);
        return;
      }
      this.mount('loading', loadingScreen(track.title, 'Building the level and the physics world…'));
      // Let the loading screen paint before the synchronous build (background tabs get no frames: don't wait for one).
      await new Promise<void>((r) => {
        requestAnimationFrame(() => setTimeout(r, 0));
        setTimeout(r, 60);
      });
      const library = await this.replays.library();
      this.screen = 'play';
      this.dom.shell.dataset['screen'] = 'play';
      this.lastResult = null;
      this.session = await GameSession.start(this.gameHost, track, {
        settings: this.settings,
        progress: this.progress,
        audio: this.audio,
        library,
        mode,
        callbacks: {
          onPauseChange: (paused, message) => this.onPauseChange(paused, message),
          onFinish: (r) => this.onFinish(r),
          onAttemptReset: () => this.onAttemptReset(),
        },
      });
      if (import.meta.env.DEV) window.__xernous = this.session.testHooks();
      const flow = new RankedFlow(this.rankedClient, track, () => this.rankedChanged(flow));
      this.ranked = flow;
      if (mode === 'full-run') void flow.probe();
      this.screenHost.hidden = true;
      this.playLayer.hidden = false;
      this.showReadyPanel();
    } catch (err: unknown) {
      this.showError(`${track.title} could not start`, describeStartError(err));
    } finally {
      this.busy = false;
    }
  }

  private leavePlay(): void {
    this.settingsPanel?.dispose();
    this.settingsPanel = null;
    if (!this.session) return;
    this.session.dispose();
    this.session = null;
    this.audio.quiet();
    this.panelHost.replaceChildren();
    this.panel = '';
    this.lastResult = null;
    this.ranked?.dispose();
    this.ranked = null;
    if (import.meta.env.DEV) delete window.__xernous;
  }

  private start(): void {
    const s = this.session;
    if (!s) return;
    this.sound();
    s.play().catch(() => {
      // The engine reported the refusal through onPauseChange; keep the panel up.
    });
  }

  private showReadyPanel(): void {
    const s = this.session!;
    const r = this.ranked;
    const ranked = r && s.mode === 'full-run' ? rankedReady(r.state, r.enabled, () => this.go(() => void this.startRanked())) : null;
    this.setPanel('ready', readyPanel(s.track, s.mode, this.settings, () => this.start(), () => this.go(() => this.exitTo('tracks')), ranked));
  }

  /** Issues a ranked attempt, then starts the run (from the ready panel, or a retry of a ranked run). */
  private async startRanked(restart = false): Promise<void> {
    const s = this.session;
    const r = this.ranked;
    if (!s || !r) return;
    if (s.mode !== 'full-run') s.setMode('full-run');
    if (!(await r.arm()) || this.session !== s) return;
    if (restart) s.restart(true);
    this.start();
  }

  private rankedChanged(flow: RankedFlow): void {
    if (flow !== this.ranked) return;
    if (this.panel === 'ready') this.showReadyPanel();
    else if ((this.panel === 'results' || this.panel === 'results-compact') && this.lastResult) this.showResults(this.panel === 'results');
  }

  /** A finished run of a ranked session: submit its recording (the capture emits it right after the finish tick). */
  private submitRanked(r: RunResult): void {
    const flow = this.ranked;
    if (!flow?.enabled || !flow.armed || !r.valid || r.mode !== 'full-run') return;
    this.rankedRun = r;
    setTimeout(() => {
      if (flow !== this.ranked || this.lastResult !== r) return;
      const rec = this.finishedRecording();
      if (rec) void flow.submit(rec);
      else this.rankedRun = null;
    }, 0);
  }

  private async showDevScores(): Promise<void> {
    let rows: Record<string, unknown>[] | string;
    try {
      rows = await this.rankedClient.devScores();
    } catch (err: unknown) {
      rows = `The development score listing is not available: ${err instanceof Error ? err.message : String(err)} (start the server with npm run server:dev).`;
    }
    this.mount('scores', devScoresScreen(rows, () => this.goBack(() => this.showMenu())));
  }

  private showPausePanel(): void {
    const s = this.session;
    if (!s) return;
    if (s.finished && this.lastResult) {
      this.showResults(true);
      return;
    }
    this.setPanel(
      'pause',
      pausePanel(this.pauseMessage, s.mode, {
        resume: () => this.start(),
        restart: () => {
          s.restart(true);
          this.start();
        },
        toggleMode: () => {
          s.setMode(s.mode === 'full-run' ? 'practice' : 'full-run');
          this.mode = s.mode;
          this.pauseMessage = s.mode === 'full-run' ? 'Full run: timed and recorded. A death or a pause ends the attempt.' : 'Practice: respawn at checkpoints. Times are not recorded.';
          this.showPausePanel();
        },
        settings: () => this.go(() => this.showSettings('pause')),
        tracks: () => this.go(() => this.exitTo('tracks')),
        menu: () => this.go(() => this.exitTo('menu')),
      }),
    );
  }

  private showResults(interactive: boolean): void {
    const s = this.session;
    const r = this.lastResult;
    if (!s || !r) return;
    const next = nextTrack(s.track.key, this.allTracks());
    this.setPanel(
      interactive ? 'results' : 'results-compact',
      resultsPanel(r, s.track, interactive, {
        retry: () => {
          if (this.ranked?.enabled) {
            void this.startRanked(true);
            return;
          }
          s.restart(true);
          this.start();
        },
        next: next ? () => this.go(() => void this.openTrack(next, this.mode)) : null,
        tracks: () => this.go(() => this.exitTo('tracks')),
        menu: () => this.go(() => this.exitTo('menu')),
        watch: () => this.go(() => void this.watchRun()),
        save: () => this.go(() => void this.saveRun()),
        note: this.saveNote,
        ranked: this.ranked ? rankedResult(this.ranked.state, this.ranked.enabled, r.valid && r.mode === 'full-run', this.rankedRun === r) : null,
      }),
    );
  }

  /** The recording of the run on the results panel (the capture emits it right after the finish tick). */
  private finishedRecording(): ReturnType<GameSession['latestRecording']> {
    const rec = this.session?.latestRecording() ?? null;
    const r = this.lastResult;
    return rec && r && rec.result.outcome === 'finished' && rec.result.seconds === r.seconds ? rec : null;
  }

  private async watchRun(): Promise<void> {
    const s = this.session;
    const rec = this.finishedRecording();
    if (!s || !rec) return;
    const track = s.track;
    const mode = s.mode;
    this.leavePlay();
    await this.replays.watch(rec, `${track.title} · this run`, track, () => void this.openTrack(track, mode));
  }

  private async saveRun(): Promise<void> {
    const s = this.session;
    const rec = this.finishedRecording();
    if (!s || !rec) return;
    this.saveNote = await this.replays.saveRecording(rec, s.track.key);
    if (this.session === s && this.panel === 'results') this.showResults(true);
  }

  private async openReplays(): Promise<void> {
    this.leavePlay();
    await this.replays.show();
  }

  private setPanel(id: string, el: HTMLElement | null): void {
    this.panel = id;
    this.panelHost.replaceChildren(...(el ? [el] : []));
    if (el && id !== 'results-compact') el.querySelector<HTMLElement>('.primary')?.focus({ preventScroll: true });
  }

  private onPauseChange(paused: boolean, message: string): void {
    if (paused) {
      this.pauseMessage = message.startsWith('Click to play') || message.startsWith('Paused') ? '' : message;
      if (this.panel === 'settings') return;
      this.showPausePanel();
      return;
    }
    if (this.panel !== 'results-compact') this.setPanel('', null);
    if (this.session?.finished && this.lastResult) this.showResults(false);
  }

  private onFinish(r: RunResult): void {
    this.lastResult = r;
    this.saveNote = '';
    this.showResults(this.session?.paused ?? false);
    this.submitRanked(r);
  }

  private onAttemptReset(): void {
    this.lastResult = null;
    this.saveNote = '';
    if (this.panel === 'results-compact') this.setPanel('', null);
  }

  private exitTo(where: 'tracks' | 'menu'): void {
    this.leavePlay();
    if (where === 'tracks') void this.showTracks();
    else this.showMenu();
  }

  // ---------------------------------------------------------------- editor

  async openEditor(projectId?: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      this.leavePlay();
      this.mount('loading', loadingScreen('Track editor', 'Opening your tracks…'));
      this.store?.close();
      this.store = null;
      this.storeTried = false;
      this.dom.shell.hidden = true;
      this.dom.editor.hidden = false;
      this.screen = 'editor';
      // Loaded on demand: the editor is a separate chunk the game's startup does not pay for.
      const { EditorApp } = await import('../editor/EditorApp');
      this.editor = await EditorApp.create(this.dom.editor, { host: this.dom.legacyHost, dom: this.dom.legacyDom, exit: this.dom.legacyExit, onExit: () => void this.closeEditor(), ...(projectId ? { projectId } : {}) });
      if (import.meta.env.DEV) window.__xernousEditor = this.editor.testHooks();
    } catch (err: unknown) {
      this.dom.editor.hidden = true;
      this.dom.shell.hidden = false;
      this.showError('The editor could not open', err instanceof Error ? err.message : String(err));
    } finally {
      this.busy = false;
    }
  }

  async closeEditor(): Promise<void> {
    const e = this.editor;
    if (!e) return;
    this.editor = null;
    await e.dispose();
    if (import.meta.env.DEV) delete window.__xernousEditor;
    this.dom.editor.hidden = true;
    this.dom.legacyHost.hidden = true;
    this.dom.shell.hidden = false;
    this.local = [];
    await this.showTracks();
  }

  // ---------------------------------------------------------------- tracks and progress

  /** Players see only indoor tracks (Phase 10); the open-air research levels stay for development builds. */
  private allTracks(): CatalogTrack[] {
    return [...builtinTracks(), ...this.local, ...(import.meta.env.DEV ? developerLevels() : [])];
  }

  private progressOf(t: CatalogTrack): TrackProgress {
    return t.recordKey ? this.progress.get(t.recordKey) : emptyProgress();
  }

  private async openStore(): Promise<ProjectStore | null> {
    if (this.store || this.storeTried) return this.store;
    this.storeTried = true;
    try {
      this.store = await ProjectStore.open();
    } catch (err: unknown) {
      this.notice = `Your local tracks are unavailable: ${err instanceof Error ? err.message : String(err)}`;
    }
    return this.store;
  }

  private async refreshLocal(): Promise<void> {
    this.loadingLocal = true;
    try {
      this.local = await localTracks(await this.openStore());
    } catch (err: unknown) {
      this.notice = `Your local tracks could not be read: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      this.loadingLocal = false;
    }
    if (this.screen === 'tracks') this.renderTracks();
  }

  private async importFile(file: File): Promise<void> {
    this.sound();
    const store = await this.openStore();
    if (!store) {
      this.notice = 'Importing needs local storage, which this browser has blocked for this site.';
      this.renderTracks();
      return;
    }
    const r = await importTrackFile(file);
    if (!r.ok) {
      this.notice = `Not imported: ${r.errors.slice(0, 3).join('; ')}`;
      this.sound('invalid');
      this.renderTracks();
      return;
    }
    const id = newProjectId();
    try {
      await store.save({ ...r.doc, id, title: titleWithSuffix(r.doc.title, ' (imported)') });
      this.notice = `Imported "${titleWithSuffix(r.doc.title, ' (imported)')}".`;
      this.selected = `local:${id}`;
    } catch (err: unknown) {
      this.notice = `Not imported: ${err instanceof Error ? err.message : String(err)}`;
    }
    await this.refreshLocal();
  }

  // ---------------------------------------------------------------- settings

  private changeSettings(next: Settings): void {
    this.settings = next;
    saveSettings(next);
    this.applyShellSettings();
    this.session?.applySettings(next);
  }

  private applyShellSettings(): void {
    const s = this.settings;
    const root = document.documentElement;
    root.style.setProperty('--ui-scale', String(s.uiScale));
    root.dataset['reducedMotion'] = String(s.reducedMotion);
    root.dataset['contrast'] = s.highContrast ? 'high' : 'normal';
    applyLineColour(s.outlineColor);
    this.audio.setVolumes({ master: s.masterVolume, music: s.musicVolume, effects: s.effectsVolume });
  }

  // ---------------------------------------------------------------- input

  private go(fn: () => void): void {
    this.sound();
    fn();
  }

  private goBack(fn: () => void): void {
    this.sound('uiBack');
    fn();
  }

  private onKey(e: KeyboardEvent): void {
    if (e.repeat || this.screen === 'editor') return;
    const target = e.target as HTMLElement | null;
    if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) && e.code !== 'Escape') return;
    if (this.screen === 'play') {
      if (e.code !== 'Enter' && e.code !== 'NumpadEnter') return;
      if (this.panel === 'ready') {
        e.preventDefault();
        this.start();
      } else if (this.panel === 'results-compact' && this.session && this.lastResult) {
        const next = nextTrack(this.session.track.key, this.allTracks());
        if (next) {
          e.preventDefault();
          this.session.release();
          void this.openTrack(next, this.mode);
        }
      }
      return;
    }
    if (e.code === 'Escape' && !this.settingsPanel?.capturing) {
      if (this.screen === 'tracks' || this.screen === 'howto' || this.screen === 'credits' || this.screen === 'error' || this.screen === 'replays') this.goBack(() => this.showMenu());
      else if (this.screen === 'settings') this.closeSettings();
    }
  }

  testHooks(): GameTestHooks {
    return {
      screen: () => this.screen,
      panel: () => this.panel,
      tracks: () => this.allTracks().map((t) => ({ key: t.key, title: t.title, source: t.source, error: t.error, recordKey: t.recordKey })),
      open: async (key, mode = 'full-run') => {
        const t = this.allTracks().find((x) => x.key === key);
        if (!t) throw new Error(`no track ${key}`);
        await this.openTrack(t, mode);
      },
      session: () => this.session?.testHooks() ?? null,
      progress: (key) => this.progress.get(key),
      settings: () => ({ ...this.settings }),
      lastResult: () => this.lastResult,
      audioState: () => this.audio.state,
      audioPlayed: () => this.audio.played,
      resources: () => liveResources(),
      viewer: () => this.replays.viewerHooks(),
      ranked: () => (this.ranked ? { enabled: this.ranked.enabled, state: this.ranked.state } : null),
    };
  }
}

/** A player-readable reason a track failed to start. */
export function describeStartError(err: unknown): string {
  if (err instanceof RendererInitError) return err.message;
  if (err instanceof LevelValidationError) return `The track data is invalid: ${err.message}`;
  const msg = err instanceof Error ? err.message : String(err);
  if (/unsafe spawns/.test(msg)) return `The track's start or a checkpoint is inside geometry or next to a hazard, so it can't be played safely. ${msg}`;
  if (/wasm|rapier|WebAssembly/i.test(msg)) return `The physics engine could not start (${msg}). Reload the page; if it keeps happening, this browser may block WebAssembly.`;
  return `Something went wrong while loading: ${msg}. Go back and try again, or reload the page.`;
}
